import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "../css/signupPage.css";
import OtpVerification from "../components/OtpVerification";
import axiosHttp from "../config/axios.interceptor";
import { AppContext } from "../contexts/AppContext";
import { AuthContext } from "../contexts/AuthContext";
import { errorResponseMessage } from "../shared/const.prop";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const SignUpForm = ({ onBackClick }) => {
  const navigate = useNavigate();
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const emailRegex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#^$%!])[A-Za-z\d@#^$%!]{8,}$/;
  const userRegex = /^[a-zA-Z0-9]*$/;

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    agreeToTerms: false,
  });
  const [err, setErr] = useState(false);
  const [passwordValidations, setPasswordValidations] = useState({
    hasUppercase: false,
    hasLowercase: false,
    isLongEnough: false,
    hasNumber: false,
    hasSpecial: false,
  });
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isOtpDisabled, setIsOtpDisabled] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleInputChange = (e) => {
    setErr(false);
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (name === "password") {
      validatePassword(value);
    }
  };

  const validatePassword = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const isLongEnough = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[@#^$%!]/.test(password);

    setPasswordValidations({
      hasUppercase,
      hasLowercase,
      isLongEnough,
      hasNumber,
      hasSpecial,
    });
  };

  const validate = () => {
    if (formData.username.trim().length < 4) {
      setErr("Username requires minimum of 4 characters length");
      return false;
    } else if (!userRegex.test(formData.username)) {
      setErr("Username cannot contain special characters");
      return false;
    } else if (!emailRegex.test(formData.email)) {
      setErr("Enter a valid email");
      return false;
    } else if (!passwordRegex.test(formData.password)) {
      setErr(
        "Password must be 8 characters and include at least one lowercase letter, one uppercase letter, one number, and one special character"
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setIsDisabled(true);
      try {
        const registerSubmitData = await axiosHttp.post(`registration`, {
          userName: formData.username,
          emailId: formData.email,
          password: btoa(formData.password),
        });
        if (
          registerSubmitData.status === 200 ||
          registerSubmitData.status === 201
        ) {
          appContext.updateNotificationState({
            show: true,
            message: "Please verify with OTP.",
            type: "success",
          });
          localStorage.setItem("userId", registerSubmitData.data.userId);
          setIsOtpModalOpen(true);
        }
      } catch (e) {
        if (e.response?.status === 429) {
          appContext.updateNotificationState({
            show: true,
            message: errorResponseMessage(e),
            type: "error",
          });
          authContext.logout();
        }
        if (e.response?.status >= 500) {
          appContext.updateNotificationState({
            show: true,
            message: "Something went wrong, please try again later.",
            type: "error",
          });
          setIsDisabled(false);
          return;
        }
        appContext.updateNotificationState({
          show: true,
          message: errorResponseMessage(e),
          type: "error",
        });
        setIsDisabled(false);
      }
    } else {
      setIsDisabled(false);
      setIsOtpModalOpen(false);
    }
  };

  const resendOtpHandler = async () => {
    try {
      const registerSubmitData = await axiosHttp.post(`registration`, {
        userName: formData.username,
        emailId: formData.email,
        password: btoa(formData.password),
      });
      if (
        registerSubmitData.status === 200 ||
        registerSubmitData.status === 201
      ) {
        appContext.updateNotificationState({
          show: true,
          message: "Otp Resent again",
          type: "success",
        });
      }
    } catch (e) {
      if (e.response?.status === 429) {
        appContext.updateNotificationState({
          show: true,
          message: errorResponseMessage(e),
          type: "error",
        });
        authContext.logout();
      }
      if (e.response?.status >= 500) {
        appContext.updateNotificationState({
          show: true,
          message: "Something went wrong, please try again later.",
          type: "error",
        });
        return;
      }
      appContext.updateNotificationState({
        show: true,
        message: errorResponseMessage(e),
        type: "error",
      });
    }
  };

  const handleOtpSubmit = async (otp) => {
    setIsOtpDisabled(true);
    try {
      const confirmSubmitData = await axiosHttp.post(`verify-account`, {
        userId: localStorage.getItem("userId"),
        code: otp,
      });
      if (
        confirmSubmitData.status === 200 ||
        confirmSubmitData.status === 201
      ) {
        setTimeout(() => {
          localStorage.setItem(
            "registerConfirm",
            JSON.stringify({
              user_id: confirmSubmitData.data.user_id,
              verification_code: confirmSubmitData.data.verification_code,
            })
          );

          navigate("../profile-setup", { replace: true, relative: "route" });
        }, 200);
      }
    } catch (error) {
      setIsOtpDisabled(false);
      if (error.response?.status === 429) {
        appContext.updateNotificationState({
          show: true,
          message: errorResponseMessage(error),
          type: "error",
        });
        authContext.logout();
      }
      if (error.response?.status === 400) {
        appContext.updateNotificationState({
          show: true,
          message: errorResponseMessage(error),
          type: "error",
        });
      }
      if (error.response?.status >= 500) {
        appContext.updateNotificationState({
          show: true,
          message: "Something went wrong, please try again later.",
          type: "error",
        });
      }
    }
  };

  return (
    <div className="signup-left">
      <div className="form-content">
        {/* Back Button */}
        <button className="back-btn" onClick={onBackClick}>
          ← Back
        </button>

        <h1>Create your Upskillmeet account</h1>

        {err && <p className="form-error">{err} </p>}

        <form className="form-content signup-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <div className="input-group">
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="john"
                required
              />
            </div>
            <p className="note">
              <b>Note:</b> Username should not contain spaces or special
              characters
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email ID</label>
            <div className="input-group">
              <input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="jdoe@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                type={passwordVisible ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Password"
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="password-toggle"
                aria-label="Toggle password visibility"
              >
                {passwordVisible ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <ul className="password-requirements">
              <li
                className={
                  passwordValidations.hasUppercase &&
                  passwordValidations.hasLowercase
                    ? "valid"
                    : ""
                }
              >
                Mix of uppercase & lowercase letters
              </li>
              <li className={passwordValidations.isLongEnough ? "valid" : ""}>
                Minimum 8 characters long
              </li>
              <li className={passwordValidations.hasNumber ? "valid" : ""}>
                Contain at least 1 number
              </li>
              <li className={passwordValidations.hasSpecial ? "valid" : ""}>
                Contain at least 1 special character (@ # ^ $ % !)
              </li>
            </ul>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                required
              />
              By creating an account, you agree to our privacy policy and cookie
              usage.
            </label>
          </div>

          <button
            type="submit"
            className="create-account-btn"
            disabled={!formData.agreeToTerms || isDisabled}
          >
            {isDisabled ? "Registering..." : "Create Account"}
          </button>
        </form>
        <div className="signup-footer">
          <p className="terms">
            Already have an account? <a href="/login">Sign in</a>
            <br />
            Read our <a href="/tnc">Terms</a> and{" "}
            <a href="/privacy-policy">Privacy Policy</a>.
          </p>
        </div>
      </div>

      {/* OTP Modal */}
      <OtpVerification
        isOpen={isOtpModalOpen}
        onClose={() => setIsOtpModalOpen(false)}
        onSubmit={handleOtpSubmit}
        isOtpDisabled={isOtpDisabled}
        resendOtpHandler={resendOtpHandler}
        title="You have got mail"
        description="Please check your email for OTP"
        btnTxt="Continue"
      />
    </div>
  );
};

export default SignUpForm;
